import { accountScopedFetch } from 'lib/accountFetch'
import { registerAccountResetHook } from 'lib/accountSession'
import { ChatMessage, AgentStatus, ToolCall } from '../utils/types'
import {
  parseSSEEvents,
  isStatusEvent,
  isTextDeltaEvent,
  isThinkingDeltaEvent,
  isToolUseEvent,
  isToolResultEvent,
  isTableEvent,
  isChartEvent,
  isErrorEvent,
  isDoneEvent,
  isResponseEvent,
  isMetadataEvent
} from '../utils/parseSSE'
import { generateAndSetThreadTitle } from '../hooks/useThreadTitleGenerator'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export interface StreamingState {
  threadId: string
  messages: ChatMessage[]
  agentStatus: AgentStatus | null
  isLoading: boolean
  error: Error | null
  parentMessageId: string | null
}

type StateListener = (state: StreamingState) => void

/**
 * Singleton manager for handling chat streaming independently of component lifecycle.
 * This allows streaming to continue even when the user navigates away.
 */
class StreamingManager {
  private state: StreamingState | null = null
  private listeners: Set<StateListener> = new Set()
  private abortController: AbortController | null = null
  private completedThreads: Map<string, StreamingState> = new Map()
  private activeRunId = 0

  /**
   * Subscribe to state changes
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener)
    // Immediately notify with current state
    if (this.state) {
      listener(this.state)
    }
    return () => this.listeners.delete(listener)
  }

  /**
   * Get current streaming state (if any)
   */
  getState(): StreamingState | null {
    return this.state
  }

  /**
   * Get completed/cached state for a thread
   */
  getCompletedState(threadId: string): StreamingState | null {
    return this.completedThreads.get(threadId) || null
  }

  /**
   * Clear cached state for a thread
   */
  clearCompletedState(threadId: string): void {
    this.completedThreads.delete(threadId)
  }

  /**
   * Check if currently streaming for a specific thread
   */
  isStreamingForThread(threadId: string): boolean {
    return this.state?.threadId === threadId && this.state?.isLoading
  }

  private notifyListeners(): void {
    const currentState = this.state
    if (currentState) {
      this.listeners.forEach((listener) => listener(currentState))
    }
  }

  private updateState(updates: Partial<StreamingState>): void {
    if (this.state) {
      this.state = { ...this.state, ...updates }
      this.notifyListeners()
    }
  }

  private updateMessage(messageId: string, updates: Partial<ChatMessage>): void {
    if (this.state) {
      this.state = {
        ...this.state,
        messages: this.state.messages.map((m) => (m.id === messageId ? { ...m, ...updates } : m))
      }
      this.notifyListeners()
    }
  }

  /**
   * Start streaming a message
   */
  async startStreaming(params: {
    accountUrl: string
    endpoint: string
    threadId: string
    content: string
    parentMessageId: string | null
    existingMessages: ChatMessage[]
    onDone?: () => void
    onError?: (error: Error) => void
    // Title generation params - if provided, will auto-generate title when stream completes
    titleGeneration?: {
      enabled: boolean
      agentRunEndpoint: string
      userId: string
      onTitleGenerated?: () => void
    }
  }): Promise<void> {
    const {
      accountUrl,
      endpoint,
      threadId,
      content,
      parentMessageId,
      existingMessages,
      onDone,
      onError,
      titleGeneration
    } = params

    // Every run gets its own id and abort controller. Starting a new run — or a reset — makes any
    // earlier run out of date, so once that older run finishes it quietly does nothing rather than
    // overwriting the newer run's messages, controller, or cache.
    this.abortController?.abort()
    const runId = ++this.activeRunId
    const controller = new AbortController()
    this.abortController = controller
    const isCurrentRun = () => this.activeRunId === runId

    // Create user message
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
      status: 'complete'
    }

    // Create placeholder for assistant message
    const assistantMessage: ChatMessage = {
      id: generateId(),
      role: 'assistant',
      content: '',
      thinking: '',
      toolCalls: [],
      charts: [],
      timestamp: new Date(),
      status: 'streaming'
    }

    // Initialize state
    this.state = {
      threadId,
      messages: [...existingMessages, userMessage, assistantMessage],
      agentStatus: null,
      isLoading: true,
      error: null,
      parentMessageId
    }
    this.notifyListeners()

    // Build request body
    const requestBody: Record<string, unknown> = {
      messages: [
        {
          role: 'user' as const,
          content: [{ type: 'text', text: content.trim() }]
        }
      ],
      thread_id: threadId,
      parent_message_id: parentMessageId || 0
    }

    try {
      const response = await accountScopedFetch(
        `${accountUrl}${endpoint}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream'
          },
          body: JSON.stringify(requestBody)
        },
        controller.signal
      )

      if (!isCurrentRun()) return

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      if (!response.body) {
        throw new Error('No response body')
      }

      // Process the SSE stream
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const toolCalls: ToolCall[] = []

      while (true) {
        const { done, value } = await reader.read()

        if (!isCurrentRun()) return

        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const { events, remainingBuffer } = parseSSEEvents(buffer)
        buffer = remainingBuffer

        for (const event of events) {
          if (isStatusEvent(event)) {
            this.updateState({
              agentStatus: {
                message: event.data.message,
                status: event.data.status,
                isActive: true
              }
            })
          } else if (isTextDeltaEvent(event)) {
            const currentMessage = this.state?.messages.find((m) => m.id === assistantMessage.id)
            if (currentMessage) {
              this.updateMessage(assistantMessage.id, {
                content: currentMessage.content + event.data.text
              })
            }
          } else if (isThinkingDeltaEvent(event)) {
            const currentMessage = this.state?.messages.find((m) => m.id === assistantMessage.id)
            if (currentMessage) {
              this.updateMessage(assistantMessage.id, {
                thinking: (currentMessage.thinking || '') + event.data.text
              })
            }
          } else if (isToolUseEvent(event)) {
            const toolCall: ToolCall = {
              id: event.data.tool_use_id,
              name: event.data.name,
              input: event.data.input
            }
            toolCalls.push(toolCall)
            this.updateMessage(assistantMessage.id, { toolCalls: [...toolCalls] })
          } else if (isToolResultEvent(event)) {
            const toolCallIndex = toolCalls.findIndex((tc) => tc.id === event.data.tool_use_id)
            if (toolCallIndex !== -1) {
              toolCalls[toolCallIndex].result = event.data.content
              if (event.data.result_set) {
                toolCalls[toolCallIndex].resultSet = event.data.result_set
                toolCalls[toolCallIndex].resultTitle = event.data.title
              }
              // Check if this is a data_to_chart tool result with embedded chart_spec
              if (toolCalls[toolCallIndex].name === 'data_to_chart' && event.data.content) {
                try {
                  // Content can be either a JSON string or already parsed object
                  const contentJson =
                    typeof event.data.content === 'string' ? JSON.parse(event.data.content) : event.data.content
                  if (contentJson.chart_spec) {
                    toolCalls[toolCallIndex].chartSpec = contentJson.chart_spec
                  }
                } catch (e) {
                  // Content is not valid JSON with chart_spec, ignore
                }
              }
              this.updateMessage(assistantMessage.id, { toolCalls: [...toolCalls] })
            }
          } else if (isTableEvent(event)) {
            const toolCallIndex = toolCalls.findIndex((tc) => tc.id === event.data.tool_use_id)
            if (toolCallIndex !== -1) {
              toolCalls[toolCallIndex].resultSet = event.data.result_set
              toolCalls[toolCallIndex].resultTitle = event.data.title
              this.updateMessage(assistantMessage.id, { toolCalls: [...toolCalls] })
            } else {
              // Table without a matching tool call — create one so the table still renders
              const toolCall: ToolCall = {
                id: event.data.tool_use_id || `table-${generateId()}`,
                name: 'table',
                input: {},
                resultSet: event.data.result_set,
                resultTitle: event.data.title
              }
              toolCalls.push(toolCall)
              this.updateMessage(assistantMessage.id, { toolCalls: [...toolCalls] })
            }
          } else if (isChartEvent(event)) {
            const toolCallIndex = toolCalls.findIndex((tc) => tc.id === event.data.tool_use_id)
            if (toolCallIndex !== -1) {
              // Chart has a matching tool call - attach it there
              toolCalls[toolCallIndex].chartSpec = event.data.chart_spec
              this.updateMessage(assistantMessage.id, { toolCalls: [...toolCalls] })
            } else {
              // Chart without a tool call (reused from history) - store directly on message
              const currentMessage = this.state?.messages.find((m) => m.id === assistantMessage.id)
              const existingCharts = currentMessage?.charts || []
              this.updateMessage(assistantMessage.id, {
                charts: [...existingCharts, event.data.chart_spec]
              })
            }
          } else if (isErrorEvent(event)) {
            const err = new Error(`${event.data.code}: ${event.data.message}`)
            this.updateState({ error: err })
            onError?.(err)
          } else if (isMetadataEvent(event)) {
            const metadataWrapper = event.data as { metadata?: { role?: string; message_id?: string | number } }
            const metadata = metadataWrapper.metadata
            if (metadata?.role === 'assistant' && metadata?.message_id) {
              this.updateState({ parentMessageId: String(metadata.message_id) })
            }
          } else if (isResponseEvent(event)) {
            // Log response event data
          } else if (isDoneEvent(event)) {
            this.updateMessage(assistantMessage.id, { status: 'complete' })
            onDone?.()
          }
        }
      }

      if (!isCurrentRun()) return

      // Stream complete
      this.updateMessage(assistantMessage.id, { status: 'complete' })
      this.updateState({ isLoading: false, agentStatus: null })

      // Cache the completed state
      if (this.state) {
        this.completedThreads.set(threadId, { ...this.state })
      }

      // Auto-generate title if requested
      if (titleGeneration?.enabled && this.state) {
        const finalAssistantMessage = this.state.messages.find((m) => m.id === assistantMessage.id)
        if (finalAssistantMessage?.content) {
          // Fire-and-forget: runs independently of component lifecycle
          generateAndSetThreadTitle({
            agentRunEndpoint: titleGeneration.agentRunEndpoint,
            userId: titleGeneration.userId,
            threadId,
            userMessage: content,
            assistantMessage: finalAssistantMessage.content,
            onSuccess: () => {
              titleGeneration.onTitleGenerated?.()
            }
          })
        } else {
          titleGeneration.onTitleGenerated?.()
        }
      }
    } catch (err) {
      if (!isCurrentRun()) return

      if ((err as Error).name === 'AbortError') {
        this.updateMessage(assistantMessage.id, { status: 'complete' })
      } else {
        const error = err instanceof Error ? err : new Error('Unknown error')
        console.error('[StreamingManager] Stream error:', error)
        this.updateState({ error })
        this.updateMessage(assistantMessage.id, { status: 'error' })
        onError?.(error)
      }
    } finally {
      if (isCurrentRun()) {
        this.updateState({ isLoading: false, agentStatus: null })
        if (this.abortController === controller) {
          this.abortController = null
        }

        // Cache the final state
        if (this.state) {
          this.completedThreads.set(threadId, { ...this.state })
        }
      }
    }
  }

  /**
   * Stop the current stream
   */
  stop(): void {
    this.abortController?.abort()
  }

  /**
   * Clear current streaming state (when starting fresh)
   */
  clear(): void {
    this.state = null
    this.abortController?.abort()
    this.abortController = null
  }

  /**
   * Clear everything on an account switch: stop any running chat stream and drop all stored ones.
   */
  reset(): void {
    // Mark any run in progress as out of date so it stops affecting anything, then abort and clear.
    this.activeRunId++
    this.abortController?.abort()
    this.abortController = null
    this.state = null
    this.completedThreads.clear()
  }
}

// Export singleton instance
export const streamingManager = new StreamingManager()

/* Clear the chat streams when the account is cleared (such as an account switch). */
registerAccountResetHook(() => streamingManager.reset())
